/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link, Typography2, typography2 } from '@coursera/cds-core';

import SubscriptionTiersImpression from 'bundles/coursera-plus/components/subscriptionTiers/SubscriptionTiersImpression';
import type { PropsFromCaller as SubscriptionTiersImpressionProps } from 'bundles/coursera-plus/components/subscriptionTiers/SubscriptionTiersImpression';
import { getLandingPageLink, logo } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { useTracker } from 'bundles/page/lib/event-pulse/react';
import HeroTotalEnrollmentCount, {
  ENROLLMENT_COUNT_THRESHOLD,
} from 'bundles/unified-description-page-common/components/modules/hero/HeroTotalEnrollmentCount';
import DottedSeparator from 'bundles/unified-description-page-common/components/shared/DottedSeparator';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

import _t from 'i18n!nls/xdp';

type PropsFromCaller = {
  enrollmentCount?: number | null;
  isSubscribedToCourseraLite?: boolean;
  disableCTA?: boolean;
  isUserEligibleToSeeCTA?: boolean;
};

type PropsToComponent = PropsFromCaller & SubscriptionTiersImpressionProps;

const styles = {
  container: css`
    display: flex;
    align-items: center;
    margin-top: calc(var(--cds-spacing-50) * -1);

    /* Avoids line wrapping / displays each item on their own line */
    @media (max-width: 390px) {
      display: inline;

      /* Dot separator */
      > span {
        display: none;
      }

      a {
        margin-top: var(--cds-spacing-150);
        display: block;
      }
    }
  `,
  courseraPlusLogoImage: css`
    position: relative;
    bottom: 1px;
    padding-left: var(--cds-spacing-50);
  `,
  link: css`
    text-decoration-color: var(--cds-color-neutral-primary);
  `,
  enrollmentCountOverrides: css`
    span {
      ${typography2.bodySecondary}
      letter-spacing: 0.14px;

      strong > span {
        font-weight: var(--cds-font-weight-600);
      }
    }
  `,
};

export const HeroEnrollmentCountWithCPlusCTA = ({
  enrollmentCount,
  isSubscribedToCPlusOrLite,
  isSubscribedToCourseraLite,
  hasSpecialization,
  partners,
  s12nId,
  courseId,
  premiumExperienceVariant,
  disableCTA,
  isUserEligibleToSeeCTA,
}: PropsToComponent) => {
  const eventingProductData = useEventingProductData();
  const track = useTracker();

  const onClick = () => {
    track('click_button', {
      button: { name: 'description_page_c_plus_learn_more' },
      pageSection: {
        sectionName: 'hero_banner',
      },
      ...eventingProductData,
    });
  };

  const courseraPlusContent = (
    <Typography2 variant="bodySecondary" component="p">
      <FormattedMessage
        message={_t('Included with{courseraPlusOrLite}')}
        courseraPlusOrLite={
          <Imgix
            src={isSubscribedToCourseraLite ? logo.LITE_FULL_BLUE_HD : logo.PLUS_FULL_BLUE}
            maxHeight={10}
            alt={isSubscribedToCourseraLite ? _t('Coursera Lite') : _t('Coursera Plus')}
            css={styles.courseraPlusLogoImage}
          />
        }
      />
    </Typography2>
  );

  const courseraPlusCTA = disableCTA ? (
    courseraPlusContent
  ) : (
    <Link
      component={TrackedLink2}
      href={getLandingPageLink()}
      trackingName="coursera_plus_banner"
      css={styles.link}
      aria-label={_t('Learn more about Coursera Plus')}
      onClick={onClick}
    >
      {courseraPlusContent}
    </Link>
  );

  const shouldDisplayEnrollmentCount = enrollmentCount && enrollmentCount > ENROLLMENT_COUNT_THRESHOLD;

  return (
    <div css={styles.container}>
      <SubscriptionTiersImpression
        isSubscribedToCPlusOrLite={isSubscribedToCPlusOrLite}
        hasSpecialization={hasSpecialization}
        partners={partners}
        premiumExperienceVariant={premiumExperienceVariant}
        courseId={courseId}
        s12nId={s12nId}
      />
      {shouldDisplayEnrollmentCount && (
        <React.Fragment>
          <div css={styles.enrollmentCountOverrides}>
            <HeroTotalEnrollmentCount count={enrollmentCount} />
          </div>
        </React.Fragment>
      )}
      {isUserEligibleToSeeCTA && (
        <React.Fragment>
          {shouldDisplayEnrollmentCount && <DottedSeparator />}
          {courseraPlusCTA}
        </React.Fragment>
      )}
    </div>
  );
};

export default HeroEnrollmentCountWithCPlusCTA;
